import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, RotateCcw, AlertCircle, Bot } from "lucide-react";
import { postChat } from "@/api";
import { useEnquiry } from "@/context/EnquiryContext";
import { useSite } from "@/context/SiteContext";

function sessionId() {
  let id = localStorage.getItem("ekta_chat_session");
  if (!id) { id = "sess-" + Math.random().toString(36).slice(2); localStorage.setItem("ekta_chat_session", id); }
  return id;
}

export default function ChatWidget() {
  const { open: openEnquiry } = useEnquiry();
  const site = useSite();
  const aiCfg = (site && site.site && site.site.ai_assistant) || {};
  const greeting = aiCfg.greeting || "Hello! I'm Ask Ekta AI. Ask me about our wire nail machines, wire drawing machines, specifications or company — or request a quote.";
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: greeting }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => { endRef.current && endRef.current.scrollIntoView({ behavior: "smooth" }); }, [msgs, open, loading]);

  const reinitialize = () => {
    localStorage.removeItem("ekta_chat_session");
    setMsgs([{ role: "assistant", content: greeting }]);
    setError("");
    setLoading(false);
  };

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput("");
    setError("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    // Watchdog: show a "taking longer" status if the reply is slow.
    let slow = setTimeout(() => setError("The assistant is taking a little longer than usual…"), 6000);
    try {
      const res = await postChat(sessionId(), q);
      setMsgs((m) => [...m, { role: "assistant", content: res.reply }]);
      setError("");
    } catch (e) {
      setError("The assistant didn't respond. You can retry or re-initialize the chat.");
    } finally { clearTimeout(slow); setLoading(false); }
  };

  // Respect the admin "Activate AI" toggle.
  if (aiCfg.enabled === false) return null;

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-20 sm:bottom-6 right-4 z-40 flex items-center gap-2 rounded-full pl-4 pr-5 py-3 shadow-lg transition-transform hover:scale-105" style={{ background: "var(--yellow)", color: "var(--brand-primary)", boxShadow: "0 8px 24px rgba(33,33,70,0.25)" }} data-testid="ai-chat-open-button">
          <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "var(--brand-primary)", color: "var(--yellow)" }}><Bot size={15} /></span> <span className="text-sm font-bold">Ask Ekta AI</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-20 sm:bottom-6 right-4 z-40 flex flex-col rounded-lg bg-white shadow-2xl border" style={{ width: "min(380px, calc(100vw - 2rem))", height: "min(540px, 70vh)", borderColor: "var(--border-c)" }} data-testid="ai-chat-panel">
          <div className="flex items-center justify-between px-4 py-3 rounded-t-lg" style={{ background: "var(--brand-primary)" }}>
            <div className="flex items-center gap-2 text-white"><span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: "var(--yellow)", color: "var(--brand-primary)" }}><Bot size={17} /></span><span className="font-bold text-sm">Ask Ekta AI</span></div>
            <div className="flex items-center gap-1">
              <button onClick={reinitialize} className="text-white/80 hover:text-white p-1" aria-label="Re-initialize chat" title="Re-initialize" data-testid="ai-chat-reinit"><RotateCcw size={17} /></button>
              <button onClick={() => setOpen(false)} className="text-white" aria-label="Close chat" data-testid="ai-chat-close"><X size={20} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "var(--bg-2)" }}>
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[80%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed" style={m.role === "user" ? { background: "var(--brand-primary)", color: "#fff" } : { background: "#fff", color: "var(--ink-2)", border: "1px solid var(--border-c)" }}>{m.content}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="rounded-lg px-3.5 py-2.5 bg-white border" style={{ borderColor: "var(--border-c)" }}><Loader2 size={16} className="animate-spin" style={{ color: "var(--brand-primary)" }} /></div></div>}
            {error && (
              <div className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs" style={{ background: "rgba(192,57,43,0.06)", border: "1px solid rgba(192,57,43,0.25)", color: "#a33" }} data-testid="ai-chat-status">
                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                <div className="flex-1">{error} <button onClick={reinitialize} className="font-bold underline" data-testid="ai-chat-reinit-inline">Re-initialize</button></div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="px-3 pt-2 pb-1 flex gap-2 flex-wrap" style={{ background: "var(--bg-2)" }}>
            <button onClick={() => { setOpen(false); openEnquiry({}); }} className="text-xs font-semibold rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}>Get a Quote</button>
            <button onClick={() => send("I would like to talk to an Ekta representative")} className="text-xs font-semibold rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--border-c)", color: "var(--muted-ink)" }}>Talk to a Representative</button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 border-t" style={{ borderColor: "var(--border-c)" }}>
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question..." className="ekta-input" data-testid="ai-chat-input" />
            <button type="submit" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-white" style={{ background: "var(--brand-primary)" }} data-testid="ai-chat-send"><Send size={16} /></button>
          </form>
        </div>
      )}
    </>
  );
}
